import type { CSSProperties } from 'react';

/**
 * Technology logos, served from the official artwork in public/brand/.
 *
 * These replace a set of hand-drawn monochrome silhouettes. The real marks
 * carry their own colours, gradients and proportions, so they are rendered as
 * <img> rather than inline SVG: a multi-stop gradient cannot be recoloured
 * with `currentColor`, and inlining 29 files would put a few hundred KB of
 * path data into the JS bundle for no benefit.
 *
 * They are same-origin static files, so this adds no third-party request.
 */

type IconProps = { className?: string; style?: CSSProperties };

export interface TechLogo {
  name: string;
  /** File under public/brand/, without the extension. */
  file: string;
  Icon: (props: IconProps) => JSX.Element;
}

/*
  OpenAI and Vercel are deliberately absent: both ship as `fill="#fff"` and
  are invisible on the light strip. Inverting them would render OpenAI's
  knot solid black, which is not its mark — better to show neither than to
  show a wrong one.
*/

/** Builds the <img> wrapper for one mark. */
function logo(name: string, file: string): TechLogo {
  function Icon({ className = '', style }: IconProps) {
    return (
      <img
        src={`/brand/${file}.svg`}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        /* object-contain so every mark sits inside the same box whatever its
           own aspect ratio — these range from square to 4:1 wordmarks. */
        className={`object-contain ${className}`}
        style={style}
      />
    );
  }
  return { name, file, Icon };
}

/*
  Two rows, split so each reads as a coherent group: the build stack on top,
  the platforms and services it runs on below. Both rows are deliberately a
  similar width so the opposing scroll directions stay balanced.
*/
export const techRowOne: TechLogo[] = [
  logo('React', 'react'),
  logo('TypeScript', 'typescript'),
  logo('Next.js', 'nextdotjs'),
  logo('Node.js', 'nodejs'),
  logo('Python', 'python'),
  logo('Swift', 'swift'),
  logo('Android', 'android'),
  logo('Tailwind CSS', 'tailwindcss'),
  logo('PostgreSQL', 'postgresql'),
  logo('MongoDB', 'mongodb'),
  logo('Redis', 'redis'),
  logo('Docker', 'docker'),
  logo('GitHub', 'github'),
  logo('Figma', 'figma'),
  logo('Linux', 'linux'),
];

export const techRowTwo: TechLogo[] = [
  logo('Claude', 'claude'),
  logo('n8n', 'n8n'),
  logo('Make', 'make'),
  logo('AWS', 'aws'),
  logo('Google Cloud', 'google'),
  logo('Firebase', 'firebase'),
  logo('Supabase', 'supabase'),
  logo('Cloudflare', 'cloudflare'),
  logo('DigitalOcean', 'digitalocean'),
  logo('Stripe', 'stripe'),
  logo('Twilio', 'twilio'),
  logo('Amazon', 'amazon'),
];

/** Every mark, for lookups by name. */
const ALL: TechLogo[] = [...techRowOne, ...techRowTwo,
  ...['Expo', 'Fastlane', 'Anthropic', 'LangChain', 'Cypress', 'Zapier'].map(name => logo(name, name.toLowerCase())),
];

/** Neutral integration symbol for technologies without bundled brand artwork. */
const integration: TechLogo = { name: 'Integration', file: '', Icon: ({ className, style }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" className={className} style={style}>
    <rect x="8" y="8" width="8" height="8" rx="2" />
    <path d="M10 3v5m4-5v5M10 16v5m4-5v5M3 10h5m-5 4h5m8-4h5m-5 4h5" />
  </svg>
)};


/**
 * Finds the mark for a stack entry, matching loosely so "Next.js" and
 * "nextjs" both resolve. Returns undefined when there is no artwork, and the
 * caller renders a text-only chip instead.
 */
export function findTechLogo(name: string): TechLogo {
  const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, string> = { reactnative: 'react', makecom: 'make', githubactions: 'github', pgvector: 'postgresql' };
  const key = aliases[normalized] ?? normalized;
  return ALL.find((l) => {
    const n = l.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return n === key || l.file === key;
  }) ?? integration;
}
